import type { ApiContactType } from '../contacts/types';

export enum ApiNotificationType {
  // v2 types
  DocumentSuccessfullyUploaded = 'DOCUMENT_UPLOADED',
  DocumentUploadFailedDueToVirus = 'DOCUMENT_UPLOAD_FAILED_VIRUS',
  SharedDocumentsReceived = 'DOCUMENTS_RECEIVED',
  SharedDocumentUnshared = 'DOCUMENT_UNSHARED',
  SharedDocumentUnsharedByRecipient = 'DOCUMENT_UNSHARED_BY_RECIPIENT',
  SharedDocumentsAccepted = 'DOCUMENTS_ACCEPTED',
  ContactRequestReceived = 'CONTACT_REQUEST',
  ContactRemoveReceived = 'CONTACT_REMOVED',
  ContactRequestAccepted = 'CONTACT_ACCEPTED',
  EmailReused = 'EMAIL_REUSED',
  DocumentsToBeRemovedAfterDeath = 'DOCUMENTS_TO_BE_REMOVED_AFTER_DEATH',
  PushedDocumentsReceived = 'PUSHED_DOCUMENTS_RECEIVED',
  PushedCertifiedDocumentReceived = 'PUSHED_CERTIFIED_DOCUMENT_RECEIVED',
  SharedDocumentByNotaryCitizen = 'DOCUMENT_UPLOADED_BY_NOTARY',
  SharedDocumentByNotaryCitizenFailed = 'DOCUMENT_UPLOADED_BY_NOTARY_FAILED',
  DocumentUploadedByThirdParty = 'DOCUMENT_UPLOADED_BY_THIRD_PARTY',
  VaultTransferReceived = 'VAULT_TRANSFER_RECEIVED',
  ExpiringInheritedVault = 'EXPIRING_INHERITED_VAULT',
  NotaryDocumentsReceived = 'NOTARY_DOCUMENTS_RECEIVED',
  PushDocumentsAccepted = 'PUSHED_DOCUMENTS_ACCEPTED',
  NotaryContactRemoved = 'NOTARY_CONTACT_REMOVED',
  NotaryDocumentUnshared = 'NOTARY_DOCUMENT_UNSHARED',
  NotaryContactAdded = 'NOTARY_CONTACT_ADDED',
  StudyContactAdded = 'STUDY_CONTACT_ADDED',
  StudyPushedDocumentsReceived = 'STUDY_PUSHED_DOCUMENTS_RECEIVED',
  NotaryAutomaticallyClaimedVault = 'NOTARY_AUTOMATICALLY_CLAIMED_VAULT',
  NotaryPassedClaimedVaultReceived = 'NOTARY_PASSED_CLAIMED_VAULT_RECEIVED',
  CommunicationReceived = 'COMMUNICATION_RECEIVED',
  StructuredCommunicationReceived = 'STRUCTURED_COMMUNICATION_RECEIVED',
}

interface DocumentData {
  documentName: string;
  documentId: string;
}

export type DocumentSuccessfullyUploadedData = DocumentData;

export type DocumentUploadFailedDueToVirusData = DocumentData;

export interface SharedDocumentReceivedData extends DocumentData {
  documentOwnerName: string;
}

export interface SharedDocumentsReceivedData {
  documentOwnerName: string;
  documentNames: string[];
  documentIds: string[];
  message?: string;
}

export interface SharedDocumentUnsharedData extends DocumentData {
  documentOwnerName: string;
}

export interface SharedDocumentUnsharedByRecipientData extends DocumentData {
  documentOwnerName: string;
}

export interface SharedDocumentRemovedData extends DocumentData {
  documentOwnerName: string;
}

export interface SharedDocumentByNotaryCitizenData extends DocumentData {
  email: string;
  employeeName: string;
  studyName: string;
  message?: string;
}

export interface SharedDocumentByNotaryCitizenFailedData {
  employeeName: string;
}

export interface SharedDocumentsAcceptedData {
  documentIds: string | string[];
  documentReceiverName: string;
  numberOfDocumentsAccepted: number;
  numberOfDocumentsRejected: number;
  recipientName: string;
  totalNumberOfDocs: string; // should be a number
  totalNumberOfDocuments: number;
}

export interface SharedDocumentsDeclinedData {
  recipientName: string;
  documentIds: string[];
  totalNumberOfDocs: string; // should be a number
}

export interface VaultTransferReceivedData {
  studyName: string;
  deceasedName: string;
  deceasedUserId: string;
}

interface ContactData {
  /** @deprecated use contactId instead */
  userId: string;
  /** @deprecated use contactName instead */
  firstName: string;
  /** @deprecated use contactName instead */
  lastName: string;
  /** @deprecated use contactEmail instead */
  emailAddress: string;
  creationDate: string;
  accepted: string; // should be a boolean
  contactId: string;
  contactEmail: string;
  contactName: string;
}

export type ContactRequestReceivedData = ContactData;

export type ContactRemoveReceivedData = ContactData;

export type ContactRequestAcceptedData = ContactData;

export type EmailReusedData = {
  email: string;
  name: string;
};

export type DocumentsToBeRemovedAfterDeathData = {
  deceasedName: string;
  removalDate: string;
};

export interface PushDocumentsReceivedData {
  documentIds: string[];
  documentNames: string[];
  message?: string;
  pushSize: string; // should be a number
  pusherName: string;
}

export interface NotaryAutomaticallyClaimedVaultData {
  nrn: string;
  vaultOwnerName: string;
}

export interface NotaryPassedClaimedVaultReceivedData {
  studyName: string;
  vaultOwnerName: string;
}

export interface PushCertifiedDocumentReceivedData extends DocumentData {
  message?: string;
  pushSize: number;
  pusherName: string;
  pusherIsContact: boolean;
}

export interface DocumentUploadedByThirdPartyData {
  thirdPartyName: string;
  documentName: string;
  message?: string;
}

export interface ExpiringInheritedVaultData {
  deceasedName: string;
  expiryDate: string;
}

export interface NotaryDocumentsReceivedData {
  documentIds: string[];
  documentNames: string[];
  message?: string;
  documentOwnerName: string;
}

export interface PushDocumentsAcceptedData {
  documentNames: string;
  documentReceiverName: string;
  numberOfDocumentsAccepted: number;
  numberOfDocumentsRejected: number;
  totalNumberOfDocuments: number;
}

export interface NotaryContactRemovedData extends ContactData {
  contactType: ApiContactType;
}

export interface NotaryContactAddedData extends ContactData {
  contactType: ApiContactType;
}

export interface StudyContactAddedData extends ContactData {
  contactType: ApiContactType;
}

export interface NotaryDocumentUnsharedData {
  documentId: string;
  documentName: string;
  documentOwnerName: string;
}

export interface CommunicationReceivedData {
  message: string;
  senderId: string;
  senderName: string;
  title: string;
}
export interface StructuredCommunicationReceivedData {
  centralisDossierId?: string;
  ownerReference?: string;
  studyName?: string;
}

type ApiNotificationData =
  | DocumentSuccessfullyUploadedData
  | DocumentUploadFailedDueToVirusData
  | SharedDocumentReceivedData
  | SharedDocumentsReceivedData
  | SharedDocumentUnsharedData
  | SharedDocumentUnsharedByRecipientData
  | SharedDocumentRemovedData
  | SharedDocumentsAcceptedData
  | SharedDocumentsDeclinedData
  | SharedDocumentByNotaryCitizenData
  | SharedDocumentByNotaryCitizenFailedData
  | ContactRequestReceivedData
  | ContactRemoveReceivedData
  | ContactRequestAcceptedData
  | EmailReusedData
  | DocumentsToBeRemovedAfterDeathData
  | PushDocumentsReceivedData
  | PushCertifiedDocumentReceivedData
  | DocumentUploadedByThirdPartyData
  | VaultTransferReceivedData
  | ExpiringInheritedVaultData
  | NotaryDocumentsReceivedData
  | PushDocumentsAcceptedData
  | NotaryContactRemovedData
  | NotaryDocumentUnsharedData
  | NotaryContactAddedData
  | StudyContactAddedData
  | CommunicationReceivedData
  | StructuredCommunicationReceivedData;

interface BaseNotification<Data = ApiNotificationData, Type = ApiNotificationType> {
  data: Data;
  id: number;
  timestamp: string;
  type: Type;
  viewed: boolean;
}

export type ApiNotificationDocumentSuccessfullyUploaded = BaseNotification<
  DocumentSuccessfullyUploadedData,
  ApiNotificationType.DocumentSuccessfullyUploaded
>;
export type ApiNotificationDocumentUploadFailedDueToVirus = BaseNotification<
  DocumentUploadFailedDueToVirusData,
  ApiNotificationType.DocumentUploadFailedDueToVirus
>;
export type ApiNotificationSharedDocumentsReceived = BaseNotification<
  SharedDocumentsReceivedData,
  ApiNotificationType.SharedDocumentsReceived
>;
export type ApiNotificationSharedDocumentUnshared = BaseNotification<
  SharedDocumentUnsharedData,
  ApiNotificationType.SharedDocumentUnshared
>;
export type ApiNotificationSharedDocumentUnsharedByRecipient = BaseNotification<
  SharedDocumentUnsharedByRecipientData,
  ApiNotificationType.SharedDocumentUnsharedByRecipient
>;
export type ApiNotificationSharedDocumentsAccepted = BaseNotification<
  SharedDocumentsAcceptedData,
  ApiNotificationType.SharedDocumentsAccepted
>;
export type ApiNotificationSharedDocumentByNotaryCitizen = BaseNotification<
  SharedDocumentByNotaryCitizenData,
  ApiNotificationType.SharedDocumentByNotaryCitizen
>;
export type ApiNotificationSharedDocumentByNotaryCitizenFailed = BaseNotification<
  SharedDocumentByNotaryCitizenFailedData,
  ApiNotificationType.SharedDocumentByNotaryCitizenFailed
>;
export type ApiNotificationContactRequestReceived = BaseNotification<
  ContactRequestReceivedData,
  ApiNotificationType.ContactRequestReceived
>;
export type ApiNotificationContactRemoveReceived = BaseNotification<
  ContactRemoveReceivedData,
  ApiNotificationType.ContactRemoveReceived
>;
export type ApiNotificationContactRequestAccepted = BaseNotification<
  ContactRequestAcceptedData,
  ApiNotificationType.ContactRequestAccepted
>;
export type ApiNotificationEmailReused = BaseNotification<
  EmailReusedData,
  ApiNotificationType.EmailReused
>;
export type ApiNotificationDocumentsToBeRemovedAfterDeath = BaseNotification<
  DocumentsToBeRemovedAfterDeathData,
  ApiNotificationType.DocumentsToBeRemovedAfterDeath
>;
export type ApiNotificationPushDocumentsReceived = BaseNotification<
  PushDocumentsReceivedData,
  ApiNotificationType.PushedDocumentsReceived
>;
export type ApiNotificationPushCertifiedDocumentReceived = BaseNotification<
  PushCertifiedDocumentReceivedData,
  ApiNotificationType.PushedCertifiedDocumentReceived
>;
export type ApiNotificationDocumentUploadedByThirdParty = BaseNotification<
  DocumentUploadedByThirdPartyData,
  ApiNotificationType.DocumentUploadedByThirdParty
>;
export type ApiNotificationVaultTransferReceived = BaseNotification<
  VaultTransferReceivedData,
  ApiNotificationType.VaultTransferReceived
>;
export type ApiNotificationExpiringInheritedVault = BaseNotification<
  ExpiringInheritedVaultData,
  ApiNotificationType.ExpiringInheritedVault
>;
export type ApiNotificationNotaryDocumentsReceived = BaseNotification<
  NotaryDocumentsReceivedData,
  ApiNotificationType.NotaryDocumentsReceived
>;
export type ApiNotificationPushedDocumentsAccepted = BaseNotification<
  PushDocumentsAcceptedData,
  ApiNotificationType.PushDocumentsAccepted
>;
export type ApiNotificationNotaryContactRemoved = BaseNotification<
  NotaryContactRemovedData,
  ApiNotificationType.NotaryContactRemoved
>;
export type ApiNotificationNotaryContactAdded = BaseNotification<
  NotaryContactAddedData,
  ApiNotificationType.NotaryContactAdded
>;
export type ApiNotificationStudyContactAdded = BaseNotification<
  StudyContactAddedData,
  ApiNotificationType.StudyContactAdded
>;
export type ApiNotificationNotaryDocumentUnshared = BaseNotification<
  NotaryDocumentUnsharedData,
  ApiNotificationType.NotaryDocumentUnshared
>;
export type ApiNotificationStudyPushedDocumentsReceived = BaseNotification<
  PushDocumentsReceivedData,
  ApiNotificationType.StudyPushedDocumentsReceived
>;
export type ApiNotificationNotaryAutomaticallyClaimedVault = BaseNotification<
  NotaryAutomaticallyClaimedVaultData,
  ApiNotificationType.NotaryAutomaticallyClaimedVault
>;
export type ApiNotificationNotaryPassedClaimedVaultReceived = BaseNotification<
  NotaryPassedClaimedVaultReceivedData,
  ApiNotificationType.NotaryPassedClaimedVaultReceived
>;

export type ApiNotificationCommunicationReceived = BaseNotification<
  CommunicationReceivedData,
  ApiNotificationType.CommunicationReceived
>;

export type ApiNotificationStructuredCommunicationReceived = BaseNotification<
  StructuredCommunicationReceivedData,
  ApiNotificationType.StructuredCommunicationReceived
>;

export type ApiNotification =
  | ApiNotificationDocumentSuccessfullyUploaded
  | ApiNotificationDocumentUploadFailedDueToVirus
  | ApiNotificationSharedDocumentsReceived
  | ApiNotificationSharedDocumentUnshared
  | ApiNotificationSharedDocumentUnsharedByRecipient
  | ApiNotificationSharedDocumentsAccepted
  | ApiNotificationSharedDocumentByNotaryCitizen
  | ApiNotificationSharedDocumentByNotaryCitizenFailed
  | ApiNotificationContactRequestReceived
  | ApiNotificationContactRemoveReceived
  | ApiNotificationContactRequestAccepted
  | ApiNotificationEmailReused
  | ApiNotificationDocumentsToBeRemovedAfterDeath
  | ApiNotificationPushDocumentsReceived
  | ApiNotificationPushCertifiedDocumentReceived
  | ApiNotificationDocumentUploadedByThirdParty
  | ApiNotificationVaultTransferReceived
  | ApiNotificationExpiringInheritedVault
  | ApiNotificationNotaryDocumentsReceived
  | ApiNotificationPushedDocumentsAccepted
  | ApiNotificationNotaryContactRemoved
  | ApiNotificationNotaryDocumentUnshared
  | ApiNotificationNotaryContactAdded
  | ApiNotificationStudyPushedDocumentsReceived
  | ApiNotificationNotaryAutomaticallyClaimedVault
  | ApiNotificationNotaryPassedClaimedVaultReceived
  | ApiNotificationStudyContactAdded
  | ApiNotificationCommunicationReceived
  | ApiNotificationStructuredCommunicationReceived;
